import logo from './logo.svg';
import { BrowserRouter, NavLink, Route, Routes } from 'react-router-dom';
import './App.css';
import { useAuth } from './AuthContext';
import { useEffect } from 'react';
import TodoList from './TodoList';
import NotFoundPage from './NotFoundPage';

const routerBase = window.__POWERED_BY_QIANKUN__ ? '/react-app' : '/';

const sections = [
  {
    title: 'Micro frontend checklist',
    description: 'A long-form section to verify the host shell handles scrolling consistently.',
    items: [
      'Confirm mount and unmount lifecycle stability.',
      'Verify host sidebar remains fixed while content scrolls.',
      'Check repeated route switches across micro apps.',
      'Validate asset loading and publicPath behavior.',
      'Observe scroll experience inside the host content pane.',
    ],
  },
  {
    title: 'Runtime observations',
    description: 'These entries add vertical space so the page is large enough to scroll naturally.',
    items: [
      'React child app inherits container height from qiankun host.',
      'Default CRA screen no longer forces viewport-height overflow.',
      'Long content should feel like part of the main app shell.',
      'Scrolling should happen in the shared content area.',
      'Layout should remain visually stable on repeated visits.',
      'Style leakage should stay contained to the child app.',
    ],
  },
  {
    title: 'Follow-up tasks',
    description: 'Use this section to test longer pages and compare behavior against the Vue2 child app.',
    items: [
      'Add route-aware headers later if the app grows.',
      'Replace demo content with real module cards.',
      'Introduce loading states for remote failures.',
      'Add visual regression coverage for shell integration.',
      'Measure mount cost after production build.',
      'Review sandbox isolation with more global styles.',
      'Test mobile behavior under the collapsed sidebar layout.',
    ],
  },
];

const insightCards = [
  {
    kicker: 'Health',
    title: 'Route Integration',
    copy: 'This page verifies that React internal history routes coexist with the qiankun host route prefix.',
    points: [
      'The host path stays under /react-app.',
      'Direct navigation to /react-app/insights should render correctly.',
      'Back and forward navigation should remain predictable.',
    ],
  },
  {
    kicker: 'Quality',
    title: 'Shared Shell',
    copy: 'Use this page to compare card-based content with the overview route and confirm the same scroll behavior.',
    points: [
      'The host sidebar should not move while content scrolls.',
      'React route transitions should not remount the host shell.',
      'Child styles should stay local to the embedded app.',
    ],
  },
  {
    kicker: 'Plan',
    title: 'Expansion Path',
    copy: 'This route can later evolve into settings, reports, or any other internal page without changing host registration.',
    points: [
      'Add nested routes if the module grows.',
      'Load page data at route level.',
      'Keep host and child concerns clearly separated.',
    ],
  },
];

/**
 * 用户信息卡片组件
 * 展示从主应用同步过来的认证状态
 */
function UserInfoCard() {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return (
      <div style={{
        padding: '1rem',
        margin: '1rem 0',
        background: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
      }}>
        <p style={{ margin: 0, color: '#856404' }}>
          ⚠️ 未登录状态（或独立运行模式）
        </p>
      </div>
    );
  }

  return (
    <div style={{
      padding: '1.5rem',
      margin: '1rem 0',
      background: 'linear-gradient(135deg, #FF6B6B 0%, #FFB88C 100%)',
      borderRadius: '12px',
      color: 'white',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <div style={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.5rem',
          fontWeight: 'bold',
        }}>
          {user.name?.charAt(0).toUpperCase() || 'U'}
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: '0.875rem', opacity: 0.9 }}>
            👋 欢迎来到 React 子应用
          </p>
          <h3 style={{ margin: '0.25rem 0', fontSize: '1.5rem' }}>
            {user.name || '未知用户'}
          </h3>
          <p style={{ margin: 0, fontSize: '0.875rem', opacity: 0.8 }}>
            📧 {user.email || ''}
          </p>
        </div>
        <div style={{
          padding: '0.5rem 1rem',
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '20px',
          fontSize: '0.875rem',
        }}>
          ✅ 已认证
        </div>
      </div>
      <div style={{
        marginTop: '1rem',
        padding: '0.75rem',
        background: 'rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        fontSize: '0.875rem',
      }}>
        <p style={{ margin: 0 }}>
          🔐 <strong>认证说明：</strong>此用户信息由主应用通过 qiankun 全局状态同步而来，
          演示了微前端架构中的认证状态共享机制。
        </p>
      </div>
    </div>
  );
}

function OverviewPage() {
  return (
    <main className="App-main">
      {sections.map((section) => (
        <section className="App-panel" key={section.title}>
          <h2>{section.title}</h2>
          <p>{section.description}</p>
          <ul>
            {section.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      ))}
    </main>
  );
}

function InsightsPage() {
  return (
    <main className="App-main">
      <section className="App-hero App-hero--soft">
        <div>
          <p className="App-kicker">Route Two</p>
          <h2>Insights Board</h2>
          <p className="App-intro App-intro--dark">
            This page proves the React child app can manage its own history routes while mounted inside qiankun.
          </p>
        </div>
      </section>

      <section className="App-cardGrid">
        {insightCards.map((card) => (
          <article className="App-panel" key={card.title}>
            <p className="App-panelKicker">{card.kicker}</p>
            <h2>{card.title}</h2>
            <p>{card.copy}</p>
            <ul>
              {card.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </article>
        ))}
      </section>
    </main>
  );
}

function AppShell() {
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    console.log('[React Sub-App] Authentication state updated:', { user, isAuthenticated });
  }, [user, isAuthenticated]);

  return (
    <div className="App">
      <header className="App-header">
        <img src={logo} className="App-logo" alt="logo" />
        <div>
          <p className="App-kicker">React Micro App</p>
          <h1>Child App Routes</h1>
          <p className="App-intro">
            Navigate between internal pages here or from the main app sidebar while staying inside the qiankun shell.
          </p>
        </div>
      </header>

      {/* 用户信息展示 - 从主应用同步的认证状态 */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 1rem' }}>
        <UserInfoCard />
      </div>

      <nav className="App-nav">
        <NavLink to="/" end>
          Overview
        </NavLink>
        <NavLink to="/insights">Insights</NavLink>
        <NavLink to="/todos">Todos</NavLink>
      </nav>

      <Routes>
        <Route path="/" element={<OverviewPage />} />
        <Route path="/insights" element={<InsightsPage />} />
        <Route path="/todos" element={<TodoList />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter basename={routerBase}>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
